import { useEffect, useRef, useState } from 'react'
import { auth } from '../firebase'

/**
 * Supported claims: admin, staff, office and manager. Workspace permissions
 * live in portalAccess.mjs; staff has Jobs and individual attendance access.
 * Master admin emails always resolve to 'admin'.
 */
import { MASTER_ADMINS } from '../config/deploy'

export function useRole() {
  const [user, setUser] = useState(() => auth.currentUser)
  const [role, setRole] = useState(null)
  const [attendanceEmployee, setAttendanceEmployee] = useState(false)
  const [loading, setLoading] = useState(true)
  const claimRequest = useRef(0)

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      const request = ++claimRequest.current
      setUser(u)
      // Never render the previous account's permissions while new claims load.
      setRole(null)
      setAttendanceEmployee(false)
      setLoading(Boolean(u))
      if (!u) {
        return
      }
      try {
        const info = await u.getIdTokenResult(true)
        const claims = info?.claims || {}
        let nextRole = claims.role || null
        // Master admin override by email
        if (MASTER_ADMINS.includes((u.email || '').toLowerCase())) {
          nextRole = 'admin'
        }
        if (request === claimRequest.current) {
          setRole(nextRole)
          setAttendanceEmployee(claims.attendanceEmployee === true)
        }
      } catch {
        if (request === claimRequest.current) setRole(null)
      } finally {
        if (request === claimRequest.current) setLoading(false)
      }
    })
    return () => { ++claimRequest.current; if (unsub) unsub() }
  }, [])

  const reloadClaims = async () => {
    const u = auth.currentUser
    if (!u) return
    const request = ++claimRequest.current
    setRole(null)
    setAttendanceEmployee(false)
    setLoading(true)
    try {
      const info = await u.getIdTokenResult(true)
      const claims = info?.claims || {}
      let nextRole = claims.role || null
      if (MASTER_ADMINS.includes((u.email || '').toLowerCase())) {
        nextRole = 'admin'
      }
      if (request === claimRequest.current) {
        setRole(nextRole)
        setAttendanceEmployee(claims.attendanceEmployee === true)
      }
    } finally {
      if (request === claimRequest.current) setLoading(false)
    }
  }

  return { user, role, attendanceEmployee, loading, reloadClaims }
}
